import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TableModule, TablePageEvent, TableRowReorderEvent } from 'primeng/table';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { AbstractView } from '@core/interfaces';
import { TablePresenter } from './table.presenter';
import { Column, StatusElement, TableViewPresenter } from './interfaces';
import { SharedModule } from '@shared/shared.module';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DragDropModule } from 'primeng/dragdrop';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputComponent } from '../input/input.component';
import { Observable, of } from 'rxjs';
import { InputSwitchChangeEvent, InputSwitchModule } from 'primeng/inputswitch';
import { Toggle } from './interfaces/toggle';
import { PipeEnum } from '@core/enums/pipe.enum';
import { CheckboxChangeEvent, CheckboxModule } from 'primeng/checkbox';
import { Checkbox } from './interfaces/checkbox';
import { SelectionModeType } from './types/selection-mode.type';
import { Store } from '@ngrx/store';

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [
    SharedModule,
    TableModule,
    ButtonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    InputNumberModule,
    InputSwitchModule,
    DragDropModule,
    InputComponent,
    CheckboxModule,
  ],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  providers: [ TablePresenter ],
})
export class TableComponent<T, Z, X, F> extends AbstractView<TablePresenter<T, Z, X, F>>
implements TableViewPresenter<T, Z, X, F>, OnInit {
  data: T[] = [];
  @Input() loading$: Observable<boolean> = of(false);
  @Input({ required: true }) data$!: Observable<T[] | null>;
  @Input({ required: true }) selectionKey!: string;
  @Input({ required: false }) showLoader: boolean = false;
  @Input() emptyMessage: string = 'No records found';
  @Input() columns: Column<X, F>[] = [];
  @Input() rows: number = 10;
  @Input() selectionMode: SelectionModeType = 'multiple';
  @Input() paginator: boolean = true;
  @Input() columnsExpandedId: string = '';
  @Input() columnsExpanded: Column[] = [];
  @Input() actionEdit: boolean = false;
  @Input() actionEditExpanded: boolean = false;
  @Input() showActionButtons: boolean = true;
  @Input() actionRemove: boolean = false;
  @Input() actionActivate?: boolean = false;
  @Input() actionDesactive?: boolean = false;
  @Input() actionStatusStandalone?: boolean = false;
  @Input() actionRemoveExpanded: boolean = false;
  @Input() enableRowCheckbox: boolean = false;
  @Input() enableRowOrder: boolean = false;
  @Input() enableRowExpanded: boolean = false;
  @Input() selectedRows: T[] = [];
  @Input() maxSelections: number|undefined;
  @Input() currentPage: number = 1;
  @Input() showDownloadButton: boolean = false;
  @Input() customIconActivate = 'pi pi-sun';
  @Input() customIconDeactivate = 'pi pi-moon';
  
  @Output() actionEditTriggered = new EventEmitter<T|Z>();
  @Output() actionRemoveTriggered = new EventEmitter<T|Z>();
  @Output() actionActivateTriggered = new EventEmitter<T|Z>();
  @Output() actionDeactivateTriggered = new EventEmitter<T|Z>();
  @Output() actionReOrderTriggered = new EventEmitter<TableRowReorderEvent>();
  @Output() actionRowEditTriggered = new EventEmitter<T[]>();
  @Output() actionRowSelectTriggered = new EventEmitter<T[]>();
  @Output() actionToggleTriggered = new EventEmitter<Toggle<T>>();
  @Output() actionCheckboxTriggered = new EventEmitter<Checkbox<T>>();
  @Output() actionRowChangeTriggered = new EventEmitter<T[]>();
  @Output() selectedRowsChange = new EventEmitter<T[]>();
  @Output() actionPageChange = new EventEmitter<number>();
  @Output() actionDownloadTriggered = new EventEmitter<T|Z>();
  
  isEnableActionButtons$ = this.presenter.isEnableActionButtons$;
  pipeEnum = PipeEnum;
  first: number = 1;
  cdr: ChangeDetectorRef;

  expandedRowKeys: { [key: string]: boolean } = {};

  constructor(
    messageService: MessageService,
    router: Router,
    store: Store,
    presenter: TablePresenter<T, Z, X, F>,
    cdr: ChangeDetectorRef,
  ) {
    super(messageService, router, store, presenter);
    this.cdr = cdr;
  }
  
  ngOnInit(): void {
    this.presenter.setView(this);
  }

  isEnableButtonActions(): boolean {
    return !!this.actionEdit ||
      !!this.actionEditExpanded ||
      !!this.actionRemove ||
      !!this.actionRemoveExpanded;
  }

  calculateColumns(): number {
    return this.presenter.calculateColumns();
  }

  hasFilterableColumns(): boolean {
    return this.presenter.hasFilterableColumns();
  }

  editElement(element: T|Z): void {
    return this.presenter.editElement(element)
  }

  removeElement(element: T): void {
    return this.presenter.removeElement(element)
  }

  statusElement(args: StatusElement<T>): void {
    return this.presenter.statusElement(args)
  }

  onRowReorder(event: TableRowReorderEvent): void {
    this.presenter.onRowReorder(event);
  }

  onRowEdit(): void {
    this.presenter.onRowEdit();
  }

  onSelectionChange(checkeds: T[]): void {
    this.presenter.onSelectionChange(checkeds);
  }

  isArray(value: T): boolean {
    return this.presenter.isArray(value);
  }

  hasMultipleDynamicValues<T extends Record<string, T>>(
    obj: T,
    path: string,
    parentPath: string,
    checkAll: boolean = true,
    expectedValue?: unknown
  ): boolean {
    return this.presenter.hasMultipleDynamicValues(obj, path, parentPath, checkAll, expectedValue);
  }

  getFirstElement<T extends Record<string, T>>(data: T): Record<string, T> {
    return data[0] ?? data;
  }

  getDynamicValue<T extends Record<string, T>>(obj: T, path: string): T|undefined {
    return this.presenter.getDynamicValue(obj, path);
  }

  setDynamicValue<T extends Record<string, T>>(row: T, path: string, value: string): void {
    return this.presenter.setDynamicValue(row, path, value);
  }

  toggleChange(event: InputSwitchChangeEvent, row: T): void {
    this.presenter.toggleChange(event, row);
  }

  checkboxChange(event: CheckboxChangeEvent, value: string, row: T): void {
    this.presenter.checkboxChange(event, value, row);
  }

  toggleRow(rowData: T): void {
    this.presenter.toggleRow(rowData);
  }

  rowDataExpanded(rowData: Z[]): Z[] {
    return this.presenter.rowDataExpanded(rowData);
  }

  onPageChange(event:TablePageEvent): void {
    this.presenter.onPageChange(event);
  }

  downloadElement(element: T|Z): void {
    this.presenter.downloadElement(element);
  }

  isObservable(data: unknown): data is Observable<T[]> {
    return this.presenter.isObservable(data);
  }
}
